import { inject } from '@angular/core';
import { CanActivateFn } from '@angular/router';
import { OAuthService } from 'angular-oauth2-oidc';

/**
 * Auth Guard - Validates token on every route navigation
 * 
 * Handles scenarios where:
 * - App is in background and automatic refresh timer didn't fire
 * - Token expired while user was inactive
 * - Token is about to expire (within 5 minutes)
 * 
 * Flow:
 * 1. Check if token is valid and not expiring soon
 * 2. If expiring soon or invalid, attempt refresh
 * 3. If refresh fails, redirect to login
 */
export const authGuard: CanActivateFn = async () => {
  const oauthService = inject(OAuthService);

  // Check if we have valid tokens
  if (!oauthService.hasValidIdToken() || !oauthService.hasValidAccessToken()) {
    console.warn('[Auth Guard] No valid tokens found, redirecting to login');
    oauthService.initLoginFlow();
    return false;
  }

  // Check if token is about to expire (within 5 minutes buffer)
  const expiration = oauthService.getAccessTokenExpiration();
  const now = Date.now();
  const fiveMinutesInMs = 5 * 60 * 1000;

  if (expiration && (expiration - now) < fiveMinutesInMs) {
    try {
      // Attempt to refresh the token
      await oauthService.refreshToken();
      return true;
    } catch (error) {
      console.error('[Auth Guard] ❌ Token refresh failed, redirecting to login:', error);
      oauthService.initLoginFlow();
      return false;
    }
  }

  // Token is valid and not expiring soon
  return true;
};
